var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { BLOG_URL_ROOT, PAGE_IDS, TEMPLATE_IDS } from "@iscr/contentful/constants";
import { useAsync } from "@iscr/xAppLib/Hooks/useAsync";
import API_service from "@iscr/xAppLib/providers/API_service";
import { ConfigurableCarouselCard } from "@iscr/contentful/components/InfoCards/ConfigurableCarouselCard";
import { contentfulToImageData } from "@iscr/contentful/functions/functions";
import { BUTTON_STYLES, BUTTON_VARIANTS } from "@iscr/contentful/components/CTA/constants";
import Carousel from "@iscr/contentful/components/Carousel";
import { CTAWrapper } from "@iscr/contentful/components/CTA/CTAWrapper";
import { PageSectionWrapper } from "@iscr/contentful/components/PageSectionWrapper";
const MAXIMUM_RELATED_POSTS = 4;
const BLOG_CARD_STYLING = {
    cardStyling: {
        backgroundColor: "bg-white",
    },
};
const CAROUSEL_CONFIG = {
    loop: false,
    align: "start",
    speed: 12,
    autoplay: false,
    displayCardCount: false,
    carouselOnDesktop: true,
    columnCount: 3,
};
const RelatedPostDetails = ({ header, subtitle, ctas, }) => {
    return (_jsxs("div", { className: "flex flex-col justify-between gap-1 p-8 h-full", children: [_jsx("h3", { className: "flex-1 flex items-center !text-lg text-is2-gray-700 font-medium", children: header }), _jsx("p", { className: "text-is2-gray-body text-sm", children: subtitle }), ctas && ctas.length > 0 && (_jsx("div", { className: "mt-4", children: ctas.map((cta, index) => (_jsx(CTAWrapper, { cta: cta }, index))) }))] }));
};
export const RelatedPosts = ({ postId, type, styling, title, tag, }) => {
    const { blogPosts, loading } = useRelatedPosts(postId, type, tag);
    const items = useMemo(() => blogPosts === null || blogPosts === void 0 ? void 0 : blogPosts.map(({ id, headerText, subtitle, imageUrl, imageAlt, ctas }) => {
        return (_jsx(ConfigurableCarouselCard, { formattedSectionStyles: styling || BLOG_CARD_STYLING, ctas: ctas, headerText: headerText, subtitle: subtitle, imageUrl: imageUrl, imageAlt: imageAlt, cardFooterComponent: _jsx(RelatedPostDetails, { header: headerText, subtitle: subtitle, ctas: ctas }) }, id));
    }), [blogPosts]);
    if (loading || !items || items.length === 0) {
        return null;
    }
    return (_jsx(_Fragment, { children: type === TEMPLATE_IDS.CONDITIONS_LANDING_PAGE ? (_jsxs(PageSectionWrapper, { children: [title && _jsx("h2", { className: "pb-16 text-center mb-0", children: title }), _jsx(Carousel, { items: items, config: CAROUSEL_CONFIG })] })) : (_jsxs(_Fragment, { children: [_jsx("h3", { className: "pt-8 px-4 text-center md:text-left", children: "More health articles" }), _jsx("div", { className: "flex flex-row mx-auto gap-4 w-full my-6", children: _jsx(Carousel, { items: items, config: CAROUSEL_CONFIG }) })] })) }));
};
const formatPostToCard = (post, type) => {
    const { title, image, slug, description } = post.fields;
    const { updatedAt } = post.sys;
    const imageData = contentfulToImageData(image);
    return {
        id: post.sys.id,
        headerText: title,
        subtitle: type === TEMPLATE_IDS.CONDITIONS_LANDING_PAGE && description
            ? description
            : updatedAt
                ? `${new Date(updatedAt).toLocaleDateString()}`
                : "",
        imageUrl: imageData === null || imageData === void 0 ? void 0 : imageData.imageUrl,
        imageAlt: (imageData === null || imageData === void 0 ? void 0 : imageData.description) || "Related post image",
        cardVariant: "default",
        formattedSectionStyles: {},
        ctas: [
            {
                buttonText: type === TEMPLATE_IDS.CONDITIONS_LANDING_PAGE ? "Continue reading" : "Read more",
                url: `/${BLOG_URL_ROOT}/${slug}`,
                variant: BUTTON_VARIANTS.BASIC,
                buttonStyling: [BUTTON_STYLES.CENTERED, BUTTON_STYLES.FULL_WIDTH],
            },
        ],
    };
};
const useRelatedPosts = (postId, type, tag) => {
    const { data: { blogPosts } = {}, loading } = useAsync(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield API_service.load_data("cms/related-blog-posts", {
                content_type: PAGE_IDS.BLOG_POST,
                order: ["-sys.publishedAt"],
                pagination: {
                    limit: MAXIMUM_RELATED_POSTS,
                    skip: 0,
                },
                content_tags: tag,
                preview: false,
                postId: postId,
            });
            if (response.status === "ok") {
                const blogPosts = response.entries.items.map((post) => formatPostToCard(post, type || ""));
                return { blogPosts };
            }
            else {
                console.error("Failed to fetch blog posts:", response);
                return { blogPosts: [] };
            }
        }
        catch (error) {
            console.error("Error fetching blog posts:", error);
            return { blogPosts: [] };
        }
    }), []);
    return { blogPosts, loading };
};
